import { useCallback, useEffect, useRef, useState } from "react";
import { fetchArticles } from "../api";
import { Article, ArticleQuery, FeedType } from "../types";

const PAGE_SIZE = 36;
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export interface UseArticlesOptions {
  feedIds: string[];
  /** True once feeds have loaded, so we do not query before filters are known. */
  ready: boolean;
  search: string;
  type?: FeedType;
}

export interface UseArticlesResult {
  articles: Article[];
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  isRefreshing: boolean;
  error: string | null;
  lastUpdated: Date | null;
  loadMore: () => void;
  refresh: () => void;
}

/**
 * Paginated article loading. Filtering and search happen server-side so each
 * response stays small; changing a filter resets back to the first page.
 */
export function useArticles({
  feedIds,
  ready,
  search,
  type,
}: UseArticlesOptions): UseArticlesResult {
  const [articles, setArticles] = useState<Article[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const controller = useRef<AbortController | null>(null);
  const loadedCount = useRef(0);
  /**
   * Identifies the request currently in flight. React can re-run the load
   * effect for the same filter set across consecutive renders; without this
   * guard that fires a duplicate request which immediately aborts the first.
   */
  const inFlight = useRef<string | null>(null);

  // A stable key for the current filter set; changing it restarts pagination.
  const feedKey = feedIds.join(",");

  const run = useCallback(
    async (query: ArticleQuery, mode: "replace" | "append" | "refresh") => {
      const key = `${mode}|${JSON.stringify(query)}`;
      if (inFlight.current === key) return;

      controller.current?.abort();
      const abort = new AbortController();
      controller.current = abort;
      inFlight.current = key;

      if (mode === "replace") setIsLoading(true);
      if (mode === "append") setIsLoadingMore(true);
      if (mode === "refresh") setIsRefreshing(true);

      try {
        const page = await fetchArticles(query, abort.signal);
        setArticles((previous) =>
          mode === "append" ? [...previous, ...page.items] : page.items
        );
        loadedCount.current =
          mode === "append"
            ? loadedCount.current + page.items.length
            : page.items.length;
        setTotal(page.total);
        setHasMore(page.hasMore);
        setLastUpdated(new Date());
        setError(null);
      } catch (caught) {
        if ((caught as Error).name === "AbortError") {
          if (inFlight.current === key) inFlight.current = null;
          return;
        }
        // A failed background refresh must not blank out content already shown.
        if (mode !== "refresh") setArticles([]);
        setError((caught as Error).message);
      } finally {
        if (inFlight.current === key) inFlight.current = null;
        setIsLoading(false);
        setIsLoadingMore(false);
        setIsRefreshing(false);
      }
    },
    []
  );

  // Initial load, and any filter/search change.
  useEffect(() => {
    if (!ready) return;
    if (feedIds.length === 0) {
      controller.current?.abort();
      setArticles([]);
      setTotal(0);
      setHasMore(false);
      setIsLoading(false);
      setError(null);
      loadedCount.current = 0;
      return;
    }
    run({ limit: PAGE_SIZE, offset: 0, feeds: feedIds, q: search, type }, "replace");
    // feedKey stands in for the feedIds array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedKey, search, type, ready, run]);

  const loadMore = useCallback(() => {
    if (isLoadingMore || isLoading || !hasMore) return;
    run(
      {
        limit: PAGE_SIZE,
        offset: loadedCount.current,
        feeds: feedIds,
        q: search,
        type,
      },
      "append"
    );
  }, [isLoadingMore, isLoading, hasMore, run, feedIds, search, type]);

  const refresh = useCallback(() => {
    if (feedIds.length === 0) return;
    run(
      {
        limit: Math.max(loadedCount.current, PAGE_SIZE),
        offset: 0,
        feeds: feedIds,
        q: search,
        type,
      },
      "refresh"
    );
  }, [run, feedIds, search, type]);

  // Poll for new items. The window is only re-requested when the tab is
  // visible, so a backgrounded embed costs nothing.
  useEffect(() => {
    if (!ready) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [refresh, ready]);

  useEffect(() => () => controller.current?.abort(), []);

  return {
    articles,
    total,
    hasMore,
    isLoading,
    isLoadingMore,
    isRefreshing,
    error,
    lastUpdated,
    loadMore,
    refresh,
  };
}
